import React, { useEffect, useState } from 'react';
import { api, resolveImg } from '../../lib/api';
import ImageUpload from '../../components/admin/ImageUpload';
import { Loader2, Plus, Pencil, Trash2, X } from 'lucide-react';

const empty = { slug: '', category: 'Update', date: new Date().toISOString().slice(0, 10), title: '', image: '', excerpt: '', body: '' };
const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const UpdatesPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // object or null
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/updates'); setItems(data); } catch (e) {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing('new'); };
  const openEdit = (u) => { setForm({ ...empty, ...u }); setEditing(u.id); };
  const close = () => { setEditing(null); setForm(empty); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { alert('Title is required'); return; }
    const payload = { ...form, slug: form.slug || slugify(form.title) };
    setSaving(true);
    try {
      if (editing === 'new') await api.post('/admin/updates', payload);
      else await api.put(`/admin/updates/${editing}`, payload);
      close(); await load();
    } catch (e) { alert('Save failed'); }
    setSaving(false);
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this update?')) return;
    try { await api.delete(`/admin/updates/${id}`); await load(); } catch (e) {}
  };

  if (loading) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-ktc-green" size={32} /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-ktc-charcoal/60">{items.length} update(s) published</p>
        <button onClick={openNew} className="inline-flex items-center gap-2 bg-ktc-green hover:bg-ktc-greenLight text-ktc-ivory text-sm font-medium px-4 py-2.5 rounded-md transition-colors"><Plus size={16} /> New Update</button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((u) => (
          <div key={u.id} className="bg-ktc-ivoryLight border border-ktc-cream rounded-xl overflow-hidden">
            <div className="h-36 bg-ktc-cream/40">{u.image && <img src={resolveImg(u.image)} alt={u.title} className="w-full h-full object-cover" />}</div>
            <div className="p-4">
              <span className="text-[10px] uppercase tracking-wider text-ktc-gold font-semibold">{u.category} · {u.date}</span>
              <h3 className="font-serif text-lg font-semibold text-ktc-green mt-1 leading-snug line-clamp-2">{u.title}</h3>
              <div className="flex items-center gap-2 mt-3">
                <button onClick={() => openEdit(u)} className="inline-flex items-center gap-1.5 text-sm text-ktc-green font-medium"><Pencil size={14} /> Edit</button>
                <button onClick={() => remove(u.id)} className="ml-auto inline-flex items-center gap-1.5 text-sm text-red-600"><Trash2 size={14} /> Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[70] bg-ktc-greenDark/90 flex items-start justify-center p-4 overflow-y-auto" onClick={close}>
          <div className="bg-ktc-ivoryLight rounded-2xl max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-ktc-cream">
              <h2 className="font-serif text-2xl font-semibold text-ktc-green">{editing === 'new' ? 'New Update' : 'Edit Update'}</h2>
              <button onClick={close} className="text-ktc-charcoal/50 hover:text-ktc-green"><X size={22} /></button>
            </div>
            <div className="p-6 space-y-4">
              <label className="block"><span className="text-sm font-medium text-ktc-charcoal/80">Title *</span><input className="ktc-input mt-1.5" value={form.title} onChange={(e) => set('title', e.target.value)} /></label>
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block"><span className="text-sm font-medium text-ktc-charcoal/80">Category</span><input className="ktc-input mt-1.5" value={form.category} onChange={(e) => set('category', e.target.value)} placeholder="e.g. Harvest, Award, Shipment" /></label>
                <label className="block"><span className="text-sm font-medium text-ktc-charcoal/80">Date</span><input type="date" className="ktc-input mt-1.5" value={form.date} onChange={(e) => set('date', e.target.value)} /></label>
              </div>
              <ImageUpload value={form.image} onChange={(v) => set('image', v)} label="Cover Image" />
              <label className="block"><span className="text-sm font-medium text-ktc-charcoal/80">Excerpt</span><textarea rows={2} className="ktc-input mt-1.5 resize-none" value={form.excerpt} onChange={(e) => set('excerpt', e.target.value)} /></label>
              <label className="block"><span className="text-sm font-medium text-ktc-charcoal/80">Body</span><textarea rows={6} className="ktc-input mt-1.5 resize-none" value={form.body} onChange={(e) => set('body', e.target.value)} /></label>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-ktc-cream">
              <button onClick={close} className="px-5 py-2.5 rounded-md border border-ktc-cream text-ktc-charcoal/70 text-sm">Cancel</button>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-ktc-green hover:bg-ktc-greenLight text-ktc-ivory text-sm font-medium px-6 py-2.5 rounded-md transition-colors">{saving && <Loader2 size={15} className="animate-spin" />} Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpdatesPanel;
